import * as React from 'react';
import { Container, Form } from 'semantic-ui-react';

import { clienteApi, Resultados } from 'src/api';
import { Contexto } from 'src/contexto';
import Preambulo, {
    Datos as PreambuloDatos,
    inicial as preambuloInicial,
} from './componentes/S1Preambulo';
import Apoderado, {
    Datos as ApoderadoDatos,
    inicial as apoderadoInicial,
} from './componentes/S2Apoderado';
import Alumnos, { Datos as AlumnosDatos, inicial as alumnosInicial } from './componentes/S3Alumnos';
import Financieros, {
    Datos as FinancierosDatos,
    inicial as financierosInicial,
} from './componentes/S4Financieros';

export type ProcesoState = Resultados<'procesoDeMatriculaDeId'>['state'];
export type ProcesoStateInicial = Omit<
    ProcesoState,
    'id' | 'tieneFirmas' | 'tieneFotocopias' | 'tieneHuellas'
>;

interface Props {
    readonly stateInicial?: ProcesoStateInicial;
    readonly soloLectura: boolean;
}

export interface State {
    matriculando: boolean;
    preambulo: PreambuloDatos;
    apoderado: ApoderadoDatos;
    alumnos: AlumnosDatos;
    financieros: FinancierosDatos;
    id?: number;
    numeroPagare?: number;
    error?: string;
}

const stateInicial: State = {
    matriculando: false,
    preambulo: { ...preambuloInicial },
    apoderado: { ...apoderadoInicial },
    alumnos: { ...alumnosInicial },
    financieros: { ...financierosInicial },
    id: undefined,
    numeroPagare: undefined,
};

class IngresoNuevaMatricula extends React.Component<Props, State> {
    public static defaultProps = {
        soloLectura: false,
    };

    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    private readonly copiarDesdeDireccionLista = [
        {
            texto: 'Apoderado - particular',
            obtenerDireccionOrigen: () => {
                return { ...this.state.apoderado.direcciones.particular };
            },
        },
        {
            texto: 'Apoderado - laboral',
            obtenerDireccionOrigen: () => {
                return { ...this.state.apoderado.direcciones.laboral };
            },
        },
        {
            texto: 'Alumno - particular',
            obtenerDireccionOrigen: () => {
                return { ...this.state.alumnos.alumnoPrincipal.direcciones.particular };
            },
        },
        {
            texto: 'Alumno - laboral',
            obtenerDireccionOrigen: () => {
                return { ...this.state.alumnos.alumnoPrincipal.direcciones.laboral };
            },
        },
    ];

    public constructor(props: Props) {
        super(props);
        if (this.props.stateInicial) {
            this.state = {
                ...stateInicial,
                ...this.props.stateInicial,
                preambulo: {
                    ...this.props.stateInicial.preambulo,
                    asesoresDisponibles:
                        this.props.stateInicial.preambulo.aNombreDe !== undefined
                            ? [this.props.stateInicial.preambulo.aNombreDe]
                            : [],
                },
                apoderado: {
                    ...this.props.stateInicial.apoderado,
                    direcciones: {
                        ...this.props.stateInicial.apoderado.direcciones,
                        particular: {
                            ...this.props.stateInicial.apoderado.direcciones.particular,
                            copiarDesdeLista: this.copiarDesdeDireccionLista,
                        },
                        laboral: {
                            ...this.props.stateInicial.apoderado.direcciones.laboral,
                            copiarDesdeLista: this.copiarDesdeDireccionLista,
                        },
                    },
                },
                alumnos: {
                    alumnoPrincipal: this.traducirAlumno(
                        this.props.stateInicial.alumnos.alumnoPrincipal,
                    ),
                    otrosAlumnos: this.props.stateInicial.alumnos.otrosAlumnos.map(otroAlumno =>
                        this.traducirAlumno(otroAlumno),
                    ),
                },
                matriculando: false,
            };

            if (!this.props.soloLectura) {
                this.state = {
                    ...this.state,
                    preambulo: {
                        ...this.props.stateInicial.preambulo,
                        aNombreDe: undefined,
                        asesoresDisponibles: [],
                    },
                };
            }
        } else {
            this.state = { ...stateInicial };
        }

        // Inyecta la lista de direcciones en cada dirección. Técnicamente, copia
        // la referencia a la lista en cada dirección. Luego ya no es necesario modificar
        // el state, dado que dicha referencia nunca cambia.
        this.state.apoderado.direcciones.particular.copiarDesdeLista = this.state.apoderado.direcciones.laboral.copiarDesdeLista = this.state.alumnos.alumnoPrincipal.direcciones.particular.copiarDesdeLista = this.state.alumnos.alumnoPrincipal.direcciones.laboral.copiarDesdeLista = this.copiarDesdeDireccionLista;
    }

    public componentDidMount() {
        if (!this.props.soloLectura) {
            this.setState(state => ({
                ...state,
                preambulo: {
                    ...state.preambulo,
                    responsable: this.context.usuarioActual.nombrePersonalCompleto(),
                },
            }));
            this.obtenerAsesores()
                .then(asesoresDisponibles => {
                    this.setState(state => ({
                        ...state,
                        preambulo: { ...state.preambulo, asesoresDisponibles },
                    }));
                })
                .catch();
        }
    }

    public render() {
        const { preambulo, apoderado, alumnos, financieros, numeroPagare } = this.state;
        const { soloLectura } = this.props;

        const mostrarPanelApoderado = !preambulo.alumnoEsApoderado;
        if (this.state.error) {
            return <h1>{this.state.error}</h1>;
        }

        if (!soloLectura) {
            return (
                <Container text>
                    Desde hoy las matrículas se deben ingresar en{' '}
                    <a href='https://betanet.redalameda.cl' target='_blank'>
                        betanet.redalameda.cl
                    </a>
                    .
                </Container>
            );
        }

        return (
            <Form>
                <Preambulo preambulo={preambulo} onChange={this.manejarCambioPreambulo} />

                <Apoderado
                    mostrar={mostrarPanelApoderado}
                    apoderado={apoderado}
                    onChange={this.manejarCambioApoderado}
                />

                <Alumnos
                    alumnoPropioApoderado={!mostrarPanelApoderado}
                    alumnos={alumnos}
                    onChange={this.manejarCambioAlumnos}
                />

                <Financieros
                    financieros={financieros}
                    onChange={this.manejarCambioFinancieros}
                    alumnos={alumnos}
                    alCambiarMontosAlumnos={this.manejarCambioAlumnos}
                    numeroPagare={numeroPagare}
                    alCambiarNumeroPagare={this.manejarCambioNumeroPagare}
                />
            </Form>
        );
    }

    private traducirAlumno(
        alumno: ProcesoState['alumnos']['alumnoPrincipal'],
    ): State['alumnos']['alumnoPrincipal'] {
        return {
            ...alumno,
            direcciones: {
                ...alumno.direcciones,
                particular: {
                    ...alumno.direcciones.particular,
                    copiarDesdeLista: this.copiarDesdeDireccionLista,
                },
                laboral: {
                    ...alumno.direcciones.laboral,
                    copiarDesdeLista: this.copiarDesdeDireccionLista,
                },
            },
            matricula: {
                ...alumno.matricula,
                detallePreu: {
                    ...alumno.matricula.detallePreu,
                },
                detalleIngles: {
                    ...alumno.matricula.detalleIngles,
                    modelo: alumno.matricula.detalleIngles.modelo,
                },
            },
        };
    }

    private readonly manejarCambioPreambulo = (preambulo: PreambuloDatos) => {
        if (this.props.soloLectura) return;
        this.setState({ preambulo });
    };

    private readonly manejarCambioApoderado = (apoderado: ApoderadoDatos) => {
        if (this.props.soloLectura) return;
        this.setState({ apoderado });
    };

    private readonly manejarCambioAlumnos = (alumnos: AlumnosDatos) => {
        if (this.props.soloLectura) return;
        this.setState({ alumnos });
    };

    private readonly manejarCambioFinancieros = (financieros: FinancierosDatos) => {
        if (this.props.soloLectura) return;
        this.setState({ financieros });
    };

    private readonly manejarCambioNumeroPagare = (numeroPagare?: number) => {
        if (this.props.soloLectura) return;
        this.setState({ numeroPagare });
    };

    private async obtenerAsesores() {
        try {
            const respuesta = await clienteApi.ejecutar('todosLosEmpleados');
            return respuesta
                .filter(empleado => empleado.funciones.includes('asesor'))
                .map(asesor => ({
                    id: asesor.id,
                    nombre: [
                        asesor.nombrePersonal.primerNombre,
                        asesor.nombrePersonal.apellidoPaterno,
                        asesor.nombrePersonal.apellidoMaterno,
                    ].join(' '),
                }));
        } catch (error) {
            this.context.mostrarModalError(error);
            return [];
        }
    }
}

export default IngresoNuevaMatricula;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/IngresoNuevaMatricula/index.tsx